import * as React from "react";
import { cn } from "src/common/utils";
import { Link } from "src/components/ui/link";
import { Tooltip } from "src/components/ui/tooltip";

// Base source interface with common fields
interface BaseSource {
  sourceId: string;
  displayText?: string;
  title?: string | null;
  description?: string | null;
  groupTags?: readonly string[] | null;
  prefetchedArtifact?: { artifactType: string; value: unknown };
}

// Discriminated union for different source types
export type Source = 
  | (BaseSource & {
      type: "artifact";
      artifactId: string; // Required for artifacts
      toolId?: string | null;
      query?: string | null;
      link?: string | null;
    })
  | (BaseSource & {
      type: "link";
      link: string; // Required for links
      toolId?: string | null;
      query?: string | null;
      artifactId?: string | null;
    })
  | (BaseSource & {
      type: "query";
      query: string; // Required for queries
      toolId?: string | null;
      link?: string | null;
      artifactId?: string | null;
    })
  | (BaseSource & {
      type: "tool";
      toolId: string; // Required for tools
      query?: string | null;
      link?: string | null;
      artifactId?: string | null;
    })
  | (BaseSource & {
      type?: string | null | undefined; // Fallback for any other type values
      toolId?: string | null;
      query?: string | null;
      link?: string | null;
      artifactId?: string | null;
    });

// Legacy interface for backward compatibility
export interface LegacySource {
  sourceId?: string;
  toolId?: string | null;
  query?: string | null;
  link?: string | null;
  artifactId?: string | null;
  type?: string | null;
  displayText?: string;
  title?: string | null;
  description?: string | null;
  groupTags?: readonly string[] | null;
}

// Type guard functions
export function isArtifactSource(source: Source): source is BaseSource & { type: "artifact"; artifactId: string } {
  return source.type === "artifact" && Boolean(source.artifactId);
}

export function isLinkSource(source: Source): source is BaseSource & { type: "link"; link: string } {
  return source.type === "link" && Boolean(source.link);
}

export function isQuerySource(source: Source): source is BaseSource & { type: "query"; query: string } {
  return source.type === "query" && Boolean(source.query);
}

export function isToolSource(source: Source): source is BaseSource & { type: "tool"; toolId: string } {
  return source.type === "tool" && Boolean(source.toolId);
}

// Helper function to create properly typed sources
export function createSource(data: LegacySource): Source {
  if (!data.sourceId) {
    throw new Error("sourceId is required for all sources");
  }
  
  const baseSource: BaseSource = {
    sourceId: data.sourceId,
    displayText: data.displayText,
    title: data.title,
    description: data.description,
    groupTags: data.groupTags,
  };

  // Determine type based on available data
  if (data.type === "artifact" && data.artifactId) {
    return {
      ...baseSource,
      type: "artifact",
      artifactId: data.artifactId,
      toolId: data.toolId,
      query: data.query,
      link: data.link,
    };
  }
  
  if (data.type === "link" && data.link) {
    return {
      ...baseSource,
      type: "link",
      link: data.link,
      toolId: data.toolId,
      query: data.query,
      artifactId: data.artifactId,
    };
  }
  
  if (data.type === "query" && data.query) {
    return {
      ...baseSource,
      type: "query",
      query: data.query,
      toolId: data.toolId,
      link: data.link,
      artifactId: data.artifactId,
    };
  }
  
  if (data.type === "tool" && data.toolId) {
    return {
      ...baseSource,
      type: "tool",
      toolId: data.toolId,
      query: data.query,
      link: data.link,
      artifactId: data.artifactId,
    };
  }
  
  // Fallback for legacy sources
  return {
    ...baseSource,
    type: data.type,
    toolId: data.toolId,
    query: data.query,
    link: data.link,
    artifactId: data.artifactId,
  };
}

interface SourceReferenceProps {
  sourceId: string;
  index: number;
  isActive: boolean;
  onClick: (sourceId: string) => void;
}

export const SourceReference: React.FC<SourceReferenceProps> = ({ 
  sourceId, 
  index, 
  isActive, 
  onClick 
}) => {
  return (
    <Tooltip content="View source" position="top">
      <Link
        onClick={() => onClick(sourceId)}
        className={cn(
          "text-primary hover:text-primary/80 px-1 py-0.5 rounded transition-colors font-mono text-xs align-middle",
          isActive ? "bg-primary/20" : "hover:bg-primary/10"
        )}
        variant="noHover"
        aria-label="View source"
      >
        [{index}]
      </Link>
    </Tooltip>
  );
};

interface MemoryReferenceProps {
  memoryId: string;
  letter: string;
  isActive: boolean;
  onClick: (memoryId: string) => void;
}

export const MemoryReference: React.FC<MemoryReferenceProps> = ({
  memoryId,
  letter,
  isActive,
  onClick
}) => {
  return (
    <Tooltip content="View memory" position="top">
      <Link
        onClick={() => onClick(memoryId)}
        className={cn(
          "text-green-400 hover:text-green-300 px-1 py-0.5 rounded transition-colors font-mono text-xs align-middle",
          isActive ? "bg-green-900/50" : "hover:bg-green-900/30"
        )}
        variant="noHover"
        aria-label="View memory"
      >
        [{letter}]
      </Link>
    </Tooltip>
  );
};

interface SourceReferenceListProps {
  sourceIds: string[];
  sourceIdToIndex: Record<string, number>;
  activeSourceId: string | null;
  onSourceClick: (sourceId: string) => void;
}

export const SourceReferenceList: React.FC<SourceReferenceListProps> = ({ 
  sourceIds, 
  sourceIdToIndex, 
  activeSourceId, 
  onSourceClick 
}) => {
  return (
    <span className="inline-flex flex-wrap gap-1 items-center">
      {sourceIds.map((sourceId, i) => (
        <React.Fragment key={`source-${sourceId}`}>
          <SourceReference
            sourceId={sourceId}
            index={sourceIdToIndex[sourceId]}
            isActive={activeSourceId === sourceId}
            onClick={onSourceClick}
          />
          {i < sourceIds.length - 1 && ", "}
        </React.Fragment>
      ))}
    </span>
  );
};